/**
 * Small formatting rules shared by every console page.
 *
 * The one that matters: a value nobody measured is the words "not measured",
 * never a 0, a blank or a dash, because a fabricated low number reads as a
 * real reading and this console exists to be trusted.
 */
export const NOT_MEASURED = 'not measured';

export const num = (v: number | null | undefined): string =>
  typeof v === 'number' && Number.isFinite(v) ? v.toLocaleString() : NOT_MEASURED;

export const money = (v: number | null | undefined): string =>
  typeof v === 'number' && Number.isFinite(v) ? `$${v.toFixed(2)}` : NOT_MEASURED;

/** "Sep 17, 08:12" in the viewer's zone, or the word for an absent instant. */
export const when = (iso: string | null | undefined, absent = 'never'): string => {
  if (!iso) return absent;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'time not recorded';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** "Sep 17" only. */
export const day = (iso: string | null | undefined, absent = 'not recorded'): string => {
  if (!iso) return absent;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'date not recorded';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/** "3 days ago", "2 hours ago", "just now"; the word for an absent instant. */
export const ago = (iso: string | null | undefined, now: number = Date.now(), absent = 'never'): string => {
  if (!iso) return absent;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'time not recorded';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 90) return 'just now';
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
  const d = Math.round(h / 24);
  if (d < 14) return `${d} ${d === 1 ? 'day' : 'days'} ago`;
  const w = Math.round(d / 7);
  if (w < 9) return `${w} ${w === 1 ? 'week' : 'weeks'} ago`;
  return day(iso);
};

export const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Hours between an instant and now; null when the instant is absent or unreadable. */
export const hoursSince = (iso: string | null | undefined, now: number = Date.now()): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : (now - t) / 3_600_000;
};

/** The change from a prior window, as a short signed label. */
export const delta = (current: number, prior: number): { label: string; dir: 'up' | 'down' | 'flat' } => {
  const d = current - prior;
  if (d === 0) return { label: 'same as prior 7 d', dir: 'flat' };
  return { label: `${d > 0 ? '+' : ''}${d.toLocaleString()} vs prior 7 d`, dir: d > 0 ? 'up' : 'down' };
};

/** YYYY-MM-DD in the viewer's zone. */
export const localDayKey = (t: number = Date.now()): string => {
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
